import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Settings,
  Gift,
  BookOpen,
  PieChart,
  ArrowLeft,
  BarChart3,
  MessageSquare,
  Cpu,
} from 'lucide-react';

import DashboardModels from './dashboard/DashboardModels';
import DashboardApiKeys from './dashboard/DashboardApiKeys';
import DashboardFallback from './dashboard/DashboardFallback';
import DashboardSystemParameters from './dashboard/DashboardSystemParameters';
import DashboardAnalytics from './dashboard/DashboardAnalytics';
import DashboardGiftCards from './dashboard/DashboardGiftCards';
import DashboardArticles from './dashboard/DashboardArticles';
import DashboardArticleDetail from './dashboard/DashboardArticleDetail';
import DashboardOrders from './dashboard/DashboardOrders';
import DashboardOverview from './dashboard/DashboardOverview';
import DashboardUsers from './dashboard/DashboardUsers';
import DashboardAdminChats from './dashboard/DashboardAdminChats';
import { WorkspaceShell, type WorkspaceNavItem } from '../components/layout';

type NavigationSubItem = {
  name: string;
  href: string;
  current: boolean;
};

type NavigationItem = {
  name: string;
  icon: WorkspaceNavItem['icon'];
  current: boolean;
  href?: string;
  key?: string;
  submenu?: NavigationSubItem[];
  badge?: number;
  defaultOpen?: boolean;
};

function DashboardLayout() {
  const location = useLocation();
  const { t } = useTranslation(['dashboard', 'common']);
  const dataAnalysisMenuKey = 'data-analysis';
  const [unreadChats, setUnreadChats] = useState(0);

  const loadBadgeCounts = useCallback(async () => {
    try {
      const chatsRes = await axios.get('/api/admin/admin-chats/stats').catch(() => ({ data: null }));
      if (chatsRes.data) setUnreadChats(chatsRes.data.unreadByAdmin || 0);
    } catch {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    loadBadgeCounts();
    const interval = setInterval(loadBadgeCounts, 60_000);
    return () => clearInterval(interval);
  }, [loadBadgeCounts]);

  const navigation: NavigationItem[] = [
    {
      name: t('nav.overview'),
      href: '/dashboard',
      icon: BarChart3,
      current: location.pathname === '/dashboard'
    },
    {
      key: dataAnalysisMenuKey,
      name: t('nav.data_analysis'),
      icon: PieChart,
      current: location.pathname.startsWith('/dashboard/data'),
      defaultOpen: true,
      submenu: [
        {
          name: t('nav.user_management'),
          href: '/dashboard/data/users',
          current: location.pathname === '/dashboard/data/users'
        },
        {
          name: t('nav.usage_statistics'),
          href: '/dashboard/data/statistics',
          current: location.pathname === '/dashboard/data/statistics'
        },
        {
          name: t('nav.system_orders'),
          href: '/dashboard/data/orders',
          current: location.pathname === '/dashboard/data/orders'
        },
        {
          name: t('nav.api_keys'),
          href: '/dashboard/data/api-keys',
          current: location.pathname === '/dashboard/data/api-keys'
        }
      ]
    },
    {
      key: 'model-requests',
      name: t('nav.model_requests'),
      icon: Cpu,
      current: location.pathname.startsWith('/dashboard/models'),
      defaultOpen: true,
      submenu: [
        {
          name: t('nav.models'),
          href: '/dashboard/models/list',
          current: location.pathname === '/dashboard/models/list'
        },
        {
          name: t('nav.fallback_stats'),
          href: '/dashboard/models/fallback',
          current: location.pathname === '/dashboard/models/fallback'
        }
      ]
    },
    {
      name: t('nav.system_parameters'),
      href: '/dashboard/system-parameters',
      icon: Settings,
      current: location.pathname === '/dashboard/system-parameters'
    },
    {
      name: t('nav.gift_cards'),
      href: '/dashboard/gift-cards',
      icon: Gift,
      current: location.pathname === '/dashboard/gift-cards'
    },
    {
      name: t('nav.admin_chats'),
      href: '/dashboard/admin-chats',
      icon: MessageSquare,
      current: location.pathname === '/dashboard/admin-chats',
      badge: unreadChats
    },
    {
      name: t('nav.articles'),
      href: '/dashboard/articles',
      icon: BookOpen,
      current: location.pathname.startsWith('/dashboard/articles')
    }
  ];

  const navItems: WorkspaceNavItem[] = navigation.map(item => ({
    label: item.name,
    path: item.href,
    icon: item.icon,
    active: item.current,
    badge: item.badge,
    defaultOpen: item.defaultOpen,
    items: item.submenu?.map(subItem => ({
      label: subItem.name,
      path: subItem.href,
      icon: item.icon,
      active: subItem.current,
    })),
  }));

  return (
    <WorkspaceShell
      title={t('common:admin_dashboard')}
      navItems={navItems}
      utilityItems={[{ label: t('nav.back_to_console'), path: '/console', icon: ArrowLeft }]}
      contentClassName="p-0"
    >
      <Routes>
        <Route path="/" element={<DashboardOverview />} />
        <Route path="/data" element={<Navigate to="/dashboard/data/statistics" replace />} />
        <Route path="/data/statistics" element={<DashboardAnalytics />} />
        <Route path="/data/orders" element={<DashboardOrders />} />
        <Route path="/data/users" element={<DashboardUsers />} />
        <Route path="/data/api-keys" element={<DashboardApiKeys />} />
        <Route path="/models" element={<Navigate to="/dashboard/models/list" replace />} />
        <Route path="/models/list" element={<DashboardModels />} />
        <Route path="/models/fallback" element={<DashboardFallback />} />
        <Route path="/admin-chats" element={<DashboardAdminChats />} />
        <Route path="/system-parameters" element={<DashboardSystemParameters />} />
        <Route path="/gift-cards" element={<DashboardGiftCards />} />
        <Route path="/articles" element={<DashboardArticles />} />
        <Route path="/articles/:id" element={<DashboardArticleDetail />} />
      </Routes>
    </WorkspaceShell>
  );
}

export default function Dashboard() {
  return <DashboardLayout />;
}
